import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { XIcon } from 'lucide-react';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  customerQueryKey,
  getCustomer,
  type UpdateCustomerRequest,
} from '@/lib/api/customers';

import { editCustomerSchema, type EditCustomerInput } from './schema';

interface Props {
  businessId: string;
  customerId: string;
  onSave: (body: UpdateCustomerRequest) => void;
  onClose: () => void;
  isSaving: boolean;
}

const DEFAULT_VALUES: EditCustomerInput = {
  serviceAddress: '',
  notes: '',
  status: 'active',
};

export function CustomerDetailModal({
  businessId,
  customerId,
  onSave,
  onClose,
  isSaving,
}: Props) {
  const { t } = useLanguage();

  const customerQuery = useQuery({
    queryKey: customerQueryKey(businessId, customerId),
    queryFn: () => getCustomer(businessId, customerId),
  });

  const form = useForm<EditCustomerInput>({
    defaultValues: DEFAULT_VALUES,
    resolver: zodResolver(editCustomerSchema),
  });

  useEffect(() => {
    if (!customerQuery.data) return;
    form.reset({
      serviceAddress: customerQuery.data.service_address ?? '',
      notes: customerQuery.data.notes ?? '',
      status: customerQuery.data.status,
    });
  }, [customerQuery.data, form]);

  function handleSubmit(data: EditCustomerInput) {
    onSave({
      service_address: data.serviceAddress || undefined,
      notes: data.notes || undefined,
      status: data.status,
    });
  }

  const customer = customerQuery.data;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-card border rounded-xl shadow-xl w-full max-w-lg flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
          <h2 className="font-semibold text-base">{customer?.full_name ?? t.loading}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors rounded-md p-1"
            data-testid="modal-close"
          >
            <XIcon className="w-4 h-4" />
          </button>
        </div>

        {customerQuery.isPending ? (
          <p className="px-6 py-8 text-sm text-muted-foreground">{t.loading}</p>
        ) : customerQuery.isError ? (
          <p className="px-6 py-8 text-sm text-destructive">{t.customerLoadError}</p>
        ) : (
          <form onSubmit={form.handleSubmit(handleSubmit)} className="overflow-y-auto flex-1">
            <div className="px-6 py-5 flex flex-col gap-4">
              {/* Read-only, denormalized from the linked user record */}
              <div className="flex flex-col gap-1.5 text-sm">
                <span className="text-muted-foreground text-xs">{t.customerEmail}</span>
                <span>{customer?.email ?? '—'}</span>
              </div>
              <div className="flex flex-col gap-1.5 text-sm">
                <span className="text-muted-foreground text-xs">{t.employeePhone}</span>
                <span>{customer?.phone ?? '—'}</span>
              </div>

              <Controller
                control={form.control}
                name="serviceAddress"
                render={({ field, fieldState }) => (
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="edit-customer-service-address">
                      {t.customerServiceAddress}
                    </Label>
                    <input
                      {...field}
                      id="edit-customer-service-address"
                      placeholder={t.customerServiceAddressPlaceholder}
                      aria-invalid={fieldState.invalid}
                      data-testid="edit-customer-service-address-input"
                      className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                    {fieldState.error && (
                      <p className="text-xs text-destructive">{fieldState.error.message}</p>
                    )}
                  </div>
                )}
              />

              <Controller
                control={form.control}
                name="notes"
                render={({ field, fieldState }) => (
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="edit-customer-notes">{t.customerNotes}</Label>
                    <textarea
                      {...field}
                      id="edit-customer-notes"
                      placeholder={t.customerNotesPlaceholder}
                      rows={3}
                      aria-invalid={fieldState.invalid}
                      data-testid="edit-customer-notes-input"
                      className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                    />
                    {fieldState.error && (
                      <p className="text-xs text-destructive">{fieldState.error.message}</p>
                    )}
                  </div>
                )}
              />

              <Controller
                control={form.control}
                name="status"
                render={({ field }) => (
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="edit-customer-status">{t.customerStatus}</Label>
                    <select
                      {...field}
                      id="edit-customer-status"
                      data-testid="edit-customer-status-select"
                      className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="active">{t.customerActive}</option>
                      <option value="inactive">{t.customerInactive}</option>
                    </select>
                  </div>
                )}
              />
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 px-6 py-4 border-t shrink-0">
              <Button type="button" variant="outline" size="sm" onClick={onClose}>
                {t.cancel}
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={isSaving}
                data-testid="save-customer-edit"
              >
                {t.saveCustomer}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
